import React, { useState, useRef, useEffect } from 'react';
import { MapPin, Phone, Mail, Clock, MessageCircle, Send, ArrowRight, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { contact, contactForm as contentForm, images } from '../../content';
import { getWhatsAppLink } from '../../content/contentUtils';
import { turnstile as turnstileConfig, contactForm as configForm } from '../../config/production';

const defaultContact = {
  phone: "(656) 536-7364",
  phoneLink: "tel:6565367364",
  email: "credito@confiare.mx",
  address: "Ciudad Juárez, Chihuahua",
  hours: "Lunes a Viernes: 9:00 AM - 6:00 PM"
};

const defaultForm = {
  title: "Envíanos un mensaje",
  fields: {
    name: { label: "Nombre completo", placeholder: "Tu nombre" },
    phone: { label: "Teléfono", placeholder: "55 1234 5678" },
    email: { label: "Correo electrónico", placeholder: "tu@email.com" },
    creditType: { label: "Tipo de crédito", placeholder: "Selecciona una opción", options: [] },
    message: { label: "Mensaje", placeholder: "Cuéntanos cómo podemos ayudarte..." }
  },
  submitButton: "Enviar mensaje",
  submittingText: "Enviando...",
  privacyText: "Al enviar este formulario, aceptas nuestro",
  privacyLinkText: "Aviso de Privacidad",
  successMessage: "¡Gracias por contactarnos! Un asesor se pondrá en contacto contigo pronto.",
  errorMessage: "Hubo un error al enviar el formulario. Por favor intenta de nuevo."
};

const FormStatus = ({ status, message, onReset }) => {
  if (status === 'success') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-green-800 mb-2">¡Mensaje enviado!</h3>
        <p className="text-green-600 mb-4">{message}</p>
        <Button onClick={onReset} variant="outline" className="border-green-300 text-green-700 hover:bg-green-100">
          Enviar otro mensaje
        </Button>
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-8 h-8 text-red-600" />
        </div>
        <h3 className="text-lg font-semibold text-red-800 mb-2">Error al enviar</h3>
        <p className="text-red-600 mb-4">{message}</p>
        <Button onClick={onReset} variant="outline" className="border-red-300 text-red-700 hover:bg-red-100">
          Intentar de nuevo
        </Button>
      </div>
    );
  }
  return null;
};

export const ContactSection = () => {
  const contactInfo = {
    phone: contact?.phone || defaultContact.phone,
    phoneLink: contact?.phoneLink || defaultContact.phoneLink,
    email: contact?.email || defaultContact.email,
    address: contact?.address || defaultContact.address,
    hours: contact?.hours || defaultContact.hours,
    mapEmbedUrl: contact?.mapEmbedUrl || "",
    mapDirectionsUrl: contact?.mapDirectionsUrl || "#"
  };

  const form = {
    title: contentForm?.title || defaultForm.title,
    fields: contentForm?.fields || defaultForm.fields,
    submitButton: contentForm?.submitButton || defaultForm.submitButton,
    submittingText: contentForm?.submittingText || defaultForm.submittingText,
    privacyText: contentForm?.privacyText || defaultForm.privacyText,
    privacyLinkText: contentForm?.privacyLinkText || defaultForm.privacyLinkText,
    successMessage: contentForm?.successMessage || defaultForm.successMessage,
    errorMessage: contentForm?.errorMessage || defaultForm.errorMessage
  };

  const formEndpoint = configForm?.endpoint || null;
  const officeImage = images?.office || "/images/oficina-confiare.jpg";

  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', creditType: '', message: '', website: ''
  });
  const [errors, setErrors] = useState({});
  const [formStatus, setFormStatus] = useState('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);

  useEffect(() => {
    if (!turnstileConfig?.enabled || !turnstileConfig?.siteKey) return;
    const initTurnstile = () => {
      if (window.turnstile && turnstileRef.current) {
        window.turnstile.render(turnstileRef.current, {
          sitekey: turnstileConfig.siteKey,
          callback: (token) => { setTurnstileToken(token); setErrors(prev => ({ ...prev, turnstile: null })); },
          'expired-callback': () => { setTurnstileToken(null); },
          'error-callback': () => { setErrors(prev => ({ ...prev, turnstile: 'Error al cargar verificación.' })); }
        });
      }
    };
    if (window.turnstile) { initTurnstile(); }
    else {
      const interval = setInterval(() => { if (window.turnstile) { initTurnstile(); clearInterval(interval); } }, 100);
      return () => clearInterval(interval);
    }
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'El nombre es requerido';
    else if (formData.name.trim().length < 3) newErrors.name = 'Mínimo 3 caracteres';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) newErrors.email = 'El correo es requerido';
    else if (!emailRegex.test(formData.email)) newErrors.email = 'Correo no válido';
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (!formData.phone.trim()) newErrors.phone = 'El teléfono es requerido';
    else if (phoneDigits.length < 10) newErrors.phone = 'Mínimo 10 dígitos';
    if (!formData.creditType) newErrors.creditType = 'Selecciona un tipo';
    if (turnstileConfig?.enabled && !turnstileToken) newErrors.turnstile = 'Completa la verificación';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setFormStatus('submitting');
    setStatusMessage('');
    const payload = {
      name: formData.name.trim(), email: formData.email.trim(), phone: formData.phone.trim(),
      creditType: formData.creditType, message: formData.message.trim(), website: formData.website,
      ...(turnstileConfig?.enabled && turnstileToken && { turnstileToken })
    };
    if (!formEndpoint) {
      console.log('📧 Datos (modo demo):', payload);
      await new Promise(r => setTimeout(r, 1500));
      setFormStatus('success');
      setStatusMessage(form.successMessage);
      resetForm();
      return;
    }
    try {
      const response = await fetch(formEndpoint, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data.success) {
        setFormStatus('success');
        setStatusMessage(data.message || form.successMessage);
        resetForm();
      } else {
        if (data.errors) setErrors(data.errors);
        setFormStatus('error');
        setStatusMessage(data.message || form.errorMessage);
      }
    } catch (error) {
      console.error('Error:', error);
      setFormStatus('error');
      setStatusMessage(form.errorMessage);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', creditType: '', message: '', website: '' });
    if (turnstileConfig?.enabled && window.turnstile) { window.turnstile.reset(); setTurnstileToken(null); }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const handleReset = () => { setFormStatus('idle'); setStatusMessage(''); setErrors({}); };

  return (
    <section id="contacto" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#CFA935] font-semibold text-sm uppercase tracking-wider">Contacto</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-[#013C5D]">Estamos aquí para ayudarte</h2>
          <p className="mt-4 text-gray-600 text-lg">¿Tienes preguntas? Nuestro equipo de asesores está listo para atenderte.</p>
        </div>
        <div className="mb-16">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl h-[400px]">
            <img src={officeImage} alt="Oficinas CONFIARÉ" className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="grid lg:grid-cols-2 gap-16">
          <div className="space-y-8">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#013C5D]/5 flex items-center justify-center flex-shrink-0"><MapPin className="w-6 h-6 text-[#CFA935]" /></div>
                <div><h4 className="font-semibold text-[#013C5D]">Dirección</h4><p className="text-gray-600 mt-1">{contactInfo.address}</p></div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#013C5D]/5 flex items-center justify-center flex-shrink-0"><Phone className="w-6 h-6 text-[#CFA935]" /></div>
                <div><h4 className="font-semibold text-[#013C5D]">Teléfono</h4><p className="text-gray-600 mt-1">{contactInfo.phone}</p></div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#013C5D]/5 flex items-center justify-center flex-shrink-0"><Mail className="w-6 h-6 text-[#CFA935]" /></div>
                <div><h4 className="font-semibold text-[#013C5D]">Correo electrónico</h4><p className="text-gray-600 mt-1">{contactInfo.email}</p></div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#013C5D]/5 flex items-center justify-center flex-shrink-0"><Clock className="w-6 h-6 text-[#CFA935]" /></div>
                <div><h4 className="font-semibold text-[#013C5D]">Horario de atención</h4><p className="text-gray-600 mt-1">{contactInfo.hours}</p></div>
              </div>
            </div>
            <div className="bg-green-50 rounded-2xl p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-green-500 flex items-center justify-center"><MessageCircle className="w-7 h-7 text-white" /></div>
                <div className="flex-1"><h4 className="font-semibold text-[#013C5D]">WhatsApp</h4><p className="text-gray-600 text-sm">Respuesta inmediata</p></div>
                <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer"><Button className="bg-green-500 hover:bg-green-600 text-white">Escribir<ArrowRight className="ml-2 w-4 h-4" /></Button></a>
              </div>
            </div>
            {contactInfo.mapEmbedUrl && (
              <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-200">
                <iframe src={contactInfo.mapEmbedUrl} width="100%" height="250" style={{ border: 0 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Ubicación CONFIARÉ" className="w-full"></iframe>
                <div className="p-4 bg-white"><a href={contactInfo.mapDirectionsUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 text-[#013C5D] hover:text-[#CFA935] font-medium transition-colors"><MapPin className="w-4 h-4" />Ver en Google Maps</a></div>
              </div>
            )}
          </div>
          <div className="bg-gray-50 rounded-3xl p-8">
            <h3 className="text-2xl font-bold text-[#013C5D] mb-6">{form.title}</h3>
            {(formStatus === 'success' || formStatus === 'error') ? (
              <FormStatus status={formStatus} message={statusMessage} onReset={handleReset} />
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                <div className="absolute opacity-0 -z-10" aria-hidden="true">
                  <input type="text" name="website" value={formData.website} onChange={(e) => handleChange('website', e.target.value)} tabIndex={-1} autoComplete="off" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-[#013C5D]">{form.fields.name?.label} <span className="text-red-500">*</span></label>
                    <Input value={formData.name} onChange={(e) => handleChange('name', e.target.value)} placeholder={form.fields.name?.placeholder} className={`h-12 bg-white ${errors.name ? 'border-red-500' : ''}`} disabled={formStatus === 'submitting'} />
                    {errors.name && <p className="text-red-500 text-xs">{errors.name}</p>}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-[#013C5D]">{form.fields.phone?.label} <span className="text-red-500">*</span></label>
                    <Input value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} placeholder={form.fields.phone?.placeholder} type="tel" className={`h-12 bg-white ${errors.phone ? 'border-red-500' : ''}`} disabled={formStatus === 'submitting'} />
                    {errors.phone && <p className="text-red-500 text-xs">{errors.phone}</p>}
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-[#013C5D]">{form.fields.email?.label} <span className="text-red-500">*</span></label>
                  <Input value={formData.email} onChange={(e) => handleChange('email', e.target.value)} placeholder={form.fields.email?.placeholder} type="email" className={`h-12 bg-white ${errors.email ? 'border-red-500' : ''}`} disabled={formStatus === 'submitting'} />
                  {errors.email && <p className="text-red-500 text-xs">{errors.email}</p>}
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-[#013C5D]">{form.fields.creditType?.label} <span className="text-red-500">*</span></label>
                  <Select value={formData.creditType} onValueChange={(val) => handleChange('creditType', val)} disabled={formStatus === 'submitting'}>
                    <SelectTrigger className={`h-12 bg-white ${errors.creditType ? 'border-red-500' : ''}`}><SelectValue placeholder={form.fields.creditType?.placeholder} /></SelectTrigger>
                    <SelectContent>{(form.fields.creditType?.options || []).map((opt, i) => <SelectItem key={i} value={opt.value}>{opt.label}</SelectItem>)}</SelectContent>
                  </Select>
                  {errors.creditType && <p className="text-red-500 text-xs">{errors.creditType}</p>}
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-[#013C5D]">{form.fields.message?.label}</label>
                  <Textarea value={formData.message} onChange={(e) => handleChange('message', e.target.value)} placeholder={form.fields.message?.placeholder} rows={4} className="bg-white resize-none" disabled={formStatus === 'submitting'} />
                </div>
                {turnstileConfig?.enabled && (
                  <div className="space-y-2">
                    <div ref={turnstileRef} className="cf-turnstile" data-sitekey={turnstileConfig.siteKey}></div>
                    {errors.turnstile && <p className="text-red-500 text-xs">{errors.turnstile}</p>}
                  </div>
                )}
                <Button type="submit" size="lg" disabled={formStatus === 'submitting'} className="w-full bg-[#CFA935] hover:bg-[#b8942f] text-[#013C5D] font-semibold h-14 disabled:opacity-70">
                  {formStatus === 'submitting' ? <><Loader2 className="mr-2 w-5 h-5 animate-spin" />{form.submittingText}</> : <>{form.submitButton}<Send className="ml-2 w-5 h-5" /></>}
                </Button>
                <p className="text-xs text-gray-500 text-center">{form.privacyText} <a href="/aviso-de-privacidad" className="text-[#CFA935] hover:underline">{form.privacyLinkText}</a></p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
